import { unlockTypesLiterals, eaterSchema } from '@inkitt/eater-db';
import { z } from 'zod';
import { c } from '../../contract';

const UnlockResponse = z.object({
  isUnlocked: z.boolean(),
  nextReleaseTimestamp: z.number().nullish(),
  countInMilliseconds: z.number(),
  totalEpisodesUnlocked: z.number(),
});

// Insufficient coins → parity with the legacy Jupiter 402 (so the client can distinguish it from
// a successful unlock instead of treating the error body as an unlock result).
const InsufficientCoinsResponse = z.object({ error: z.string() });

/**
 * Client-supplied idempotency key for paid unlocks (DEV-6217, D1). Identifies the unlock *intent*,
 * so every retry of the same intent collapses onto one debit via the unique index on
 * `points_ledger.transactionId`. Optional: unpaid unlock types don't need it, and app versions
 * released before this field existed must keep working (they fall back to a server-generated id,
 * i.e. exactly today's behaviour).
 */
const transactionId = z.string().min(1).max(255).optional();

export const seriesRouter = c.router(
  {
    unlockHistory: {
      summary: 'Gets the unlocks for all series',
      method: 'GET',
      path: '/unlocks/history',
      query: z.object({
        offset: z.number().optional(),
      }),
      responses: {
        200: c.type<(typeof eaterSchema.unlockHistory.$inferSelect)[]>(),
      },
    },
    unlock: {
      summary: 'Unlocks an episode for a user',
      method: 'PUT',
      path: '/episodes/unlock',
      body: z.object({
        storyId: z.string(),
        currentEpisode: z.number().min(0),
        isForced: z.boolean().optional(),
        // On zod 4, `z.number()` already rejects Infinity/NaN; `.finite()` is kept as explicit
        // intent (and insurance against semantics changing again), not as the guard (review).
        // Deliberately still allows 0: shipped clients pass `priceService.getSkipPrice()`, which
        // is 0 for the dynamic-price-free cohort. `pointsAmount` remains client-authoritative —
        // see `trackPricingClaim`. Omitting the field on a `'points'` unlock is rejected by the
        // service (402), not here: legacy clients omit `unlockType`, so only the controller's
        // inference knows whether the request is actually paid.
        pointsAmount: z.number().finite().min(0).optional(),
        waitTimeMinutes: z.number().min(0).optional(),
        unlockType: z.enum(unlockTypesLiterals).optional(),
        timezoneOffsetMinutes: z.number(),
        transactionId,
      }),
      responses: {
        200: UnlockResponse,
        402: InsufficientCoinsResponse,
      },
    },
    skip: {
      summary: 'Skips the countdown for the current episode',
      method: 'PUT',
      path: '/episodes/skip',
      body: z.object({
        storyId: z.string(),
        timezoneOffsetMinutes: z.number(),
        episodesAmount: z.number().min(1),
        isForced: z.boolean().optional(),
        // Deliberately not `.positive()`, and deliberately not a cross-field `refine` on
        // `unlockType` either (one would be possible — paywall.router.ts has one): legacy clients
        // OMIT `unlockType`, so only the controller's inference knows whether a request is
        // actually paid, and the service owns the 402-vs-400 parity with legacy Jupiter. Shipped
        // clients also send `costPerItem: 0` on skips that are never charged (entitled /
        // free-book / dynamic-price-free — and that last cohort arrives AS `'points'`), so even a
        // type-aware contract could not reject zeros safely. Zero/fractional policy lives in the
        // service's charging rules (`isUsableUnlockPrice` / `isUsableCostPerItem`); the service
        // rejects malformed (negative/non-finite) prices for every unlockType. `.finite()` is
        // explicit intent — zod 4's `z.number()` already rejects Infinity/NaN (review).
        costPerItem: z.number().finite().optional(),
        dynamicPrice: z.number().finite().optional(),
        unlockType: z.enum(unlockTypesLiterals).optional(),
        transactionId,
      }),
      responses: {
        200: UnlockResponse,
        402: InsufficientCoinsResponse,
      },
    },
  },
  { pathPrefix: '/series' },
);
